import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import { FulfillmentFlatTeamSettingGroupById } from "./groups";

export type FulfillmentFlatTeamSettingKey =
  | "outboundLabels/defaultShipFromLocationExternalId"
  | "outboundLabels/address/internationalAddressOutreach"
  | "outboundLabels/documents/packingSlip/perParcelSlips"
  | "outboundLabels/documents/pickList/emphasizeQuantity"
  | "outboundLabels/documents/shippingLabel/customSectionEnabled"
  | "outboundLabels/disableKeyboardOnScan"
  | "outboundLabels/scanVerify/manualQuantityEntry"
  | "outboundLabels/scanVerify/moveScannedItemToTop"
  | "outboundLabels/scanVerify/moveVerifiedItemsToBottom"
  | "outboundLabels/scanVerify/labelAutoPrintTrigger"
  | "outboundLabels/internalNotifications/notifyOnCarrierConnectionInterrupted"
  | "outboundLabels/wms/transferPutawayOrder"
  | "outboundLabels/wms/excludeEmptyTransferDestinations"
  | "outboundLabels/wms/receivingMode"
  | "outboundLabels/wms/pickSession/forceSingleItemPickSessions"
  | "outboundLabels/wms/pickSession/scanOncePerProduct"
  | "outboundLabels/wms/defaultReplenishmentQuantity"
  | "outboundLabels/wms/dashboard"
  | "outboundLabels/wms/pickSession/scanType"
  | "outboundLabels/wms/pickSession/defaultOrderCount"
  | "outboundLabels/wms/pickSession/maxItemCount"
  | "outboundLabels/wms/bulkPickSession"
  | "outboundLabels/wms/pickSession/stalePickedRelease"
  | "outboundLabels/wms/pickSession/sameBinSortMode"
  | "outboundLabels/wms/showOrderNotesInPickSession"
  | "outboundLabels/wms/preferFullPickTitle"
  | "outboundLabels/wms/pickSession/allowPaymentPendingPicks"
  | "outboundLabels/wms/pickSession/zonePickingMode"
  | "outboundLabels/rejectUnknownItemsFromPicks"
  | "outboundLabels/wms/hideManualToteAssignment"
  | "outboundLabels/wms/preferRedoBinsOverMarketplace"
  | "outboundLabels/wms/lotTracking"
  | "outboundLabels/wms/barcodeLabelSize"
  | "outboundLabels/wms/binNaming"
  | "outboundLabels/wms/defaultFulfillmentLocationExternalId";

const shipFromLocationKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/defaultShipFromLocationExternalId";
const shipFromLocationGroupId = "fulfillment.ship_from_locations" as const;

const internationalAddressOutreachKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/address/internationalAddressOutreach";
const addressGroupId = "fulfillment.address" as const;

const perParcelSlipsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/packingSlip/perParcelSlips";
const emphasizeQuantityKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/pickList/emphasizeQuantity";
const shippingLabelCustomSectionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/shippingLabel/customSectionEnabled";
const printingGroupId = "fulfillment.printing" as const;

const manualQuantityEntryKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/manualQuantityEntry";
const moveScannedItemToTopKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/moveScannedItemToTop";
const moveVerifiedItemsToBottomKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/moveVerifiedItemsToBottom";
const labelAutoPrintTriggerKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/labelAutoPrintTrigger";
const disableKeyboardOnScanKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/disableKeyboardOnScan";
const scanToVerifyGroupId = "fulfillment.scan_to_verify" as const;

const carrierConnectionInterruptedKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/internalNotifications/notifyOnCarrierConnectionInterrupted";
const outboundNotificationsGroupId =
  "fulfillment.outbound_notifications" as const;

const transferPutawayOrderKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/transferPutawayOrder";
const excludeEmptyTransferDestinationsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/excludeEmptyTransferDestinations";
const receivingModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/receivingMode";
const transfersGroupId = "fulfillment.transfers" as const;

const forceSingleItemPickSessionsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/forceSingleItemPickSessions";
const scanOncePerProductKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/scanOncePerProduct";
const pickSessionsGroupId = "fulfillment.pick_sessions" as const;

const defaultReplenishmentQuantityKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/defaultReplenishmentQuantity";
const replenishmentGroupId = "fulfillment.replenishment" as const;

const dashboardActionsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/dashboard";
const dashboardGroupId = "fulfillment.dashboard" as const;
const workOrdersGroupId = "fulfillment.work_orders" as const;
const generalGroupId = "fulfillment.general" as const;
const scanTypeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/scanType";
const defaultOrderCountKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/defaultOrderCount";
const maxItemCountKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/maxItemCount";
const bulkPickSessionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/bulkPickSession";
const stalePickedReleaseKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/stalePickedRelease";
const sameBinSortModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/sameBinSortMode";
const showOrderNotesInPickSessionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/showOrderNotesInPickSession";
const preferFullPickTitleKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/preferFullPickTitle";
const allowPaymentPendingPicksKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/allowPaymentPendingPicks";
const zonePickingModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/zonePickingMode";
const rejectUnknownItemsFromPicksKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/rejectUnknownItemsFromPicks";
const hideManualToteAssignmentKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/hideManualToteAssignment";
const preferRedoBinsOverMarketplaceKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/preferRedoBinsOverMarketplace";
const lotTrackingKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/lotTracking";
const barcodeLabelSizeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/barcodeLabelSize";
const binNamingKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/binNaming";
const defaultFulfillmentLocationExternalIdKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/defaultFulfillmentLocationExternalId";

export const FulfillmentFlatTeamSettingCatalogDefinitions = [
  {
    id: shipFromLocationKey,
    groupId: shipFromLocationGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default ship from location",
        description:
          "Selects the ship-from location used automatically when creating a manual order.",
        synonyms: [
          "default ship from location",
          "default warehouse",
          "manual order location",
          "shipping origin default",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[shipFromLocationGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: shipFromLocationKey },
  },
  {
    id: disableKeyboardOnScanKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Disable manual entry in the scan barcode field",
        description:
          "Prevents non-admin operators from typing or pasting into the scan barcode field while continuing to accept hardware barcode scans. Separately enabled manual quantity entry remains available.",
        synonyms: [
          "disable manual barcode entry",
          "barcode scanner only",
          "prevent typing barcode",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: disableKeyboardOnScanKey },
  },
  {
    id: moveScannedItemToTopKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Move scanned item to top of the list",
        description:
          "Keeps partially scanned items at the top of the verification list, ordered with the most recently scanned item first.",
        synonyms: [
          "move scanned item to top",
          "pin scanned items",
          "sort active scans first",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: moveScannedItemToTopKey },
  },
  {
    id: moveVerifiedItemsToBottomKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Move verified items to bottom of the list",
        description:
          "Moves fully verified items below the remaining items after a brief completion animation.",
        synonyms: [
          "move verified items to bottom",
          "sort completed scans last",
          "hide verified items below",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: moveVerifiedItemsToBottomKey },
  },
  {
    id: manualQuantityEntryKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label:
          "Allow users to manually enter item quantity when line item quantity is greater than",
        description:
          "Lets operators type the verified quantity for larger line items instead of scanning every unit. The threshold determines which line items show an editable quantity input.",
        synonyms: [
          "manual scan quantity",
          "scan to verify quantity",
          "enter verified item quantity",
          "bulk item verification",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: manualQuantityEntryKey },
  },
  {
    id: labelAutoPrintTriggerKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Label auto-print trigger",
        description:
          "Chooses when scan to verify prints the shipping label on its own: when the first item is scanned, when all items are scanned, or never. Only one option can be on at a time.",
        synonyms: [
          "label auto-print trigger",
          "print label when first item is scanned",
          "print label when all items are scanned",
          "auto print shipping label",
          "automatic label printing",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: labelAutoPrintTriggerKey },
  },
  {
    id: internationalAddressOutreachKey,
    groupId: addressGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Include international orders",
        description:
          "Applies address verification failure handling to orders shipping outside the US. When on, international orders get the same correction emails and holds as domestic ones; when off, Redo takes no action on an international address it could not verify. Off by default because international addresses verify less reliably, so turning it on increases both emails and holds.",
        synonyms: [
          "international address verification",
          "international address emails",
          "non-US addresses",
          "overseas orders address",
          "address outreach international",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[addressGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: internationalAddressOutreachKey,
    },
  },
  {
    id: perParcelSlipsKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Print a packing slip per parcel",
        description:
          "When an order ships in more than one box, prints a separate packing slip for each box listing only the items allocated to it, and names that box in the slip's footer. Off by default: with it off, a multi-box order gets one slip listing every item in the order.",
        synonyms: [
          "packing slip per parcel",
          "packing slip per box",
          "one slip per box",
          "slip for each package",
          "multi parcel packing slips",
          "split packing slip by box",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: perParcelSlipsKey },
  },
  {
    id: emphasizeQuantityKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Emphasize quantity",
        description:
          "Draws an outline circle around quantities greater than 1 on pick lists so pickers can spot multi-unit lines. On by default.",
        synonyms: [
          "emphasize quantity",
          "pick list emphasize quantity",
          "pick list quantity circle",
          "outline quantity",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: emphasizeQuantityKey },
  },
  {
    id: shippingLabelCustomSectionKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Custom label section",
        description:
          "Shrinks the carrier's shipping label to print your enabled label fields in a section at the bottom, styled and previewed in the label layout editor. Off by default; when off, labels print exactly as the carrier returns them.",
        synonyms: [
          "custom label section",
          "shipping label layout",
          "label layout editor",
          "custom content on shipping label",
          "add design to bottom of label",
          "shrink shipping label",
        ],
        risk: {
          level: "medium",
          reason:
            "Changes every printed shipping label for affected carriers, and some carriers may reject labels with customizations.",
        },
        availability: {
          conditions: [
            {
              type: "evaluator",
              evaluator: "featureFlag.enabled",
              featureFlag: "shipping-label-builder",
            },
          ],
          unavailableBehavior: "hidden",
          reason:
            "Only available while the label layout editor is rolled out to the team.",
        },
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: shippingLabelCustomSectionKey,
    },
  },
  {
    id: carrierConnectionInterruptedKey,
    groupId: outboundNotificationsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "When a carrier account needs to be reconnected",
        description:
          "Emails the outbound labels notification addresses when a connected carrier account (such as UPS) loses its authorization and needs to be reconnected.",
        synonyms: [
          "carrier connection interrupted",
          "carrier disconnected email",
          "ups reauthorization alert",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[outboundNotificationsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: carrierConnectionInterruptedKey,
    },
  },
  {
    id: transferPutawayOrderKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Transfer put-away order",
        description:
          "Chooses whether the mobile app walks transfer put-away in the order items were scanned or along the warehouse map route through the suggested bins.",
        synonyms: [
          "transfer put-away order",
          "put-away walk order",
          "transfer route order",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: transferPutawayOrderKey },
  },
  {
    id: excludeEmptyTransferDestinationsKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Don't suggest empty destination bins",
        description:
          "When on, transfer put-away only suggests destination bins that already hold the product. Bins where the product is at 0 are skipped; if every bin is empty, the product has no suggested bin and staff choose one.",
        synonyms: [
          "skip empty bins",
          "empty destination bins",
          "zero quantity bins",
          "transfer destination suggestion",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: excludeEmptyTransferDestinationsKey,
    },
  },
  {
    id: receivingModeKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Receiving mode",
        description:
          "Chooses which receiving flows the warehouse dashboard offers: receive into the receiving area, receive and place straight into bins, or both.",
        synonyms: [
          "receiving mode",
          "receive and place only",
          "hide receive and place",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: receivingModeKey },
  },
  {
    id: forceSingleItemPickSessionsKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Pick session strategy",
        description:
          "Forces every pick session to be single-item, so single and multi-item orders are picked together into shared totes and the multi-item option is hidden from pickers.",
        synonyms: [
          "force single-item pick sessions",
          "single item pick sessions only",
          "hide multi-item pick sessions",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: forceSingleItemPickSessionsKey,
    },
  },
  {
    id: scanOncePerProductKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Scan once per product",
        description:
          "In single-item and scan-at-wall multi-item sessions, scanning a product with several units to pick opens a confirmation prefilled with the full quantity instead of requiring one scan per unit.",
        synonyms: [
          "scan once per product",
          "confirm total pick quantity",
          "skip scanning every unit",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: scanOncePerProductKey },
  },
  {
    id: defaultReplenishmentQuantityKey,
    groupId: replenishmentGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default replenishment quantity",
        description:
          "Number of units moved into a pickable bin each time a product is replenished. Never more than the source bin has available. When blank, replenishment tops bins up to cover open demand.",
        synonyms: [
          "default replenishment quantity",
          "units per replenishment",
          "replenishment amount",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[replenishmentGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: defaultReplenishmentQuantityKey,
    },
  },
  {
    id: dashboardActionsKey,
    groupId: dashboardGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Dashboard actions",
        description:
          "Chooses which actions the warehouse dashboard shows to everyone, with per-role overrides for warehouse users, packers, pickers, and replenishers.",
        synonyms: [
          "dashboard actions",
          "hide warehouse dashboard actions",
          "dashboard actions by role",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[dashboardGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: dashboardActionsKey },
  },
  {
    id: scanTypeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Tote scanning",
        description:
          "Chooses whether pickers scan totes at the wall or at each bin while picking.",
        synonyms: ["tote scanning", "scan at wall"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: scanTypeKey },
  },
  {
    id: defaultOrderCountKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default order count",
        description:
          "Sets how many orders a new single-item and multi-item pick session suggests by default.",
        synonyms: ["default order count", "orders per pick session"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: defaultOrderCountKey },
  },
  {
    id: maxItemCountKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Item limit for multi-item sessions",
        description: "Caps how many items a multi-item pick session can hold.",
        synonyms: [
          "item limit for multi-item sessions",
          "max items per pick session",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: maxItemCountKey },
  },
  {
    id: bulkPickSessionKey,
    groupId: workOrdersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Bulk pick session limits",
        description:
          "Caps the work orders and pick lines a bulk pick session can include, and the default work order count it suggests.",
        synonyms: ["bulk pick session limits", "bulk pick max work orders"],
      },
      group: FulfillmentFlatTeamSettingGroupById[workOrdersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: bulkPickSessionKey },
  },
  {
    id: stalePickedReleaseKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Stale pick session release",
        description:
          "Releases orders back to the queue after they sit in the picked status for too many days.",
        synonyms: ["stale pick session release", "release stuck picked orders"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: stalePickedReleaseKey },
  },
  {
    id: sameBinSortModeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Same-bin sort order",
        description:
          "Breaks ties between items routed to the same bin when a pick wave is created.",
        synonyms: ["same-bin sort order", "pick wave tiebreaker"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: sameBinSortModeKey },
  },
  {
    id: showOrderNotesInPickSessionKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Order notes in pick sessions",
        description:
          "Shows the customer's order note on each item while picking.",
        synonyms: [
          "order notes in pick sessions",
          "show order note to pickers",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: showOrderNotesInPickSessionKey,
    },
  },
  {
    id: preferFullPickTitleKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Show full product titles while picking",
        description:
          "Shows each item's product and variant title together while picking, instead of only the variant or its size and color.",
        synonyms: [
          "show full product titles while picking",
          "full pick title",
          "product and variant title when picking",
          "product title in pick sessions",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: preferFullPickTitleKey },
  },
  {
    id: allowPaymentPendingPicksKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Allow picking payment-pending orders",
        description:
          "Lets pickers start orders whose payment is still pending.",
        synonyms: [
          "allow picking payment-pending orders",
          "pick unpaid orders",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: allowPaymentPendingPicksKey },
  },
  {
    id: zonePickingModeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Zone picking",
        description:
          "Chooses whether pick sessions are split by warehouse zone and in what order zones are worked.",
        synonyms: ["zone picking", "pick by zone"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: zonePickingModeKey },
  },
  {
    id: rejectUnknownItemsFromPicksKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Items not on the pick list",
        description:
          "Rejects scanned items that are not part of the current pick instead of adding them.",
        synonyms: [
          "items not on the pick list",
          "reject unknown items when picking",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: rejectUnknownItemsFromPicksKey,
    },
  },
  {
    id: hideManualToteAssignmentKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Manual tote assignment",
        description:
          "Hides the manual tote assignment step so totes are always assigned by scanning.",
        synonyms: ["manual tote assignment", "hide tote assignment"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: hideManualToteAssignmentKey },
  },
  {
    id: preferRedoBinsOverMarketplaceKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Which bins to pull from first",
        description:
          "Chooses whether picking uses bins assigned in Redo before bins from Shopify or other marketplace metafields. When on, bin changes in Redo also update the Shopify bin metafield.",
        synonyms: ["which bins to pull from first", "bin source preference"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: preferRedoBinsOverMarketplaceKey,
    },
  },
  {
    id: lotTrackingKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Lot tracking",
        description:
          "Excludes expired lots from the sales channel and blocks picking for a period after receiving lot-tracked inventory.",
        synonyms: ["lot tracking settings", "exclude expired lots"],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: lotTrackingKey },
  },
  {
    id: barcodeLabelSizeKey,
    groupId: workOrdersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Work order label size",
        description:
          "Chooses the default label size when printing barcode labels from work orders.",
        synonyms: ["work order barcode label size", "bin label size"],
      },
      group: FulfillmentFlatTeamSettingGroupById[workOrdersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: barcodeLabelSizeKey },
  },
  {
    id: binNamingKey,
    groupId: generalGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Bin naming",
        description:
          "Defines how bin names are built from row, rack, shelf and bin, including the advanced naming template.",
        synonyms: ["bin naming", "bin naming template"],
      },
      group: FulfillmentFlatTeamSettingGroupById[generalGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: binNamingKey },
  },
  {
    id: defaultFulfillmentLocationExternalIdKey,
    groupId: generalGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default fulfillment location",
        description: "Chooses the location warehouse devices open by default.",
        synonyms: [
          "default fulfillment location",
          "default warehouse location",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[generalGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: defaultFulfillmentLocationExternalIdKey,
    },
  },
] satisfies readonly SettingDefinition<
  FulfillmentFlatTeamSettingKey,
  | typeof shipFromLocationGroupId
  | typeof addressGroupId
  | typeof printingGroupId
  | typeof scanToVerifyGroupId
  | typeof outboundNotificationsGroupId
  | typeof transfersGroupId
  | typeof pickSessionsGroupId
  | typeof replenishmentGroupId
  | typeof dashboardGroupId
  | typeof workOrdersGroupId
  | typeof generalGroupId,
  TeamSettingsSettingValueSource<FulfillmentFlatTeamSettingKey>
>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentFlatTeamSettingMetadataByKey = Object.fromEntries(
  FulfillmentFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<FulfillmentFlatTeamSettingKey, SettingMetadata>;
